import React, {
    useState, useEffect, useMemo, useRef,
} from 'react';

import { Button, Form, Image as ImageBootstrap } from 'react-bootstrap';
import Cropper from 'react-cropper';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';

import { StdModalDialog } from './dialog';

import 'cropperjs/dist/cropper.css';

import '../css/fast-image-load.css';

function FastImageLoad({
    label, name, image, minSize, altMinSize, restrictions, onChange, required, is_not_valid, className,
}) {
    const [imagePreview, setImagePreview] = useState(null);
    const [isDialogActive, setDialogActive] = useState(false);
    const [sizeBoundary, setSizeBoundary] = useState(minSize);

    useEffect(() => {
        if (imagePreview) {
            setDialogActive(true);

            return;
        }

        setDialogActive(false);
    }, [imagePreview]);

    const cropperRef = useRef(null);

    let labelStyle = {};

    if (is_not_valid) {
        labelStyle = { backgroundColor: 'pink' };
    }

    function toggleSizeBoundary() {
        setSizeBoundary(sizeBoundary === minSize ? altMinSize : minSize);
    }

    function openEditDialog() {
        const url = image ? image.replace('media.vkplay.ru', window.location.hostname) : '';
        setImagePreview(url);
    }

    function closeDialog() {
        setDialogActive(false);
        setImagePreview(null);
    }

    function fileChangeHandler(files) {
        const reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
    }

    function cropHandler(event) {
        const minWidth = parseInt(sizeBoundary.split('x')[0]);
        const minHeight = parseInt(sizeBoundary.split('x')[1]);

        if ((event.detail.width < minWidth || event.detail.height < minHeight) && cropperRef.current.cropper) {
            cropperRef.current.cropper.setData(
                {
                    ...event.detail,
                    width:
                        event.detail.width < minWidth
                            ? minWidth
                            : event.detail.width,
                    height:
                        event.detail.height < minHeight
                            ? minHeight
                            : event.detail.height,
                },
            );
        }
    }

    function saveImage() {
        const result = cropperRef.current.cropper.getCroppedCanvas().toDataURL('image/jpeg', 0.75);

        onChange({
            target: { type: 'cropped_img', value: result, id: name },
            currentTarget: { type: 'cropped_img', value: result, id: name },
        });

        closeDialog();
    }

    const loadedImage = useMemo(() => (
        <>
            <ImageBootstrap className="fast-image-load__image" src={image} responsive="true" />
            <span className="help-block">{restrictions}</span>
            <div className="fast-image-load__buttons">
                <Dropzone
                    accept="image/jpeg, image/png, image/webp"
                    onDrop={fileChangeHandler}
                >
                    {({ getRootProps, getInputProps }) => (
                        <div {...getRootProps()} className="dropzone dropzone_small" id="dropZone">
                            <input {...getInputProps()} />
                            <Button>Заменить</Button>
                        </div>
                    )}
                </Dropzone>
                <Button onClick={openEditDialog} variant="outline-secondary">Редактировать</Button>
            </div>
        </>
    ), [image, openEditDialog, restrictions]);

    const dialogBody = useMemo(() => {
        const minWidth = parseInt(sizeBoundary.split('x')[0]);
        const minHeight = parseInt(sizeBoundary.split('x')[1]);

        const img = new Image();
        img.src = imagePreview;

        img.onload = () => {
            if (img.width < minWidth || img.height < minHeight) {
                setImagePreview(null);
                toast.warn('Слишком маленькое изображение');
            }
        };

        return (
            <Cropper
                aspectRatio={minWidth / minHeight}
                viewMode={1}
                ref={cropperRef}
                src={imagePreview}
                guides={false}
                crop={cropHandler}
            />
        );
    }, [cropHandler, imagePreview, sizeBoundary]);

    const dialogFooter = useMemo(() => {
        if (!altMinSize) {
            return null;
        }

        return <Button variant="outline-secondary" onClick={toggleSizeBoundary}>Соотношение сторон</Button>;
    }, [altMinSize, toggleSizeBoundary]);

    const dropZone = useMemo(() => (
        <>
            <Dropzone
                accept="image/jpeg, image/png, image/webp"
                onDrop={fileChangeHandler}
            >
                {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()} className="dropzone" id="dropZone">
                        <input {...getInputProps()} />
                        <p>Перетащите файл в эту область</p>
                        <p>
                            Рекомендуемый размер - не менее
                            {sizeBoundary}
                            {' '}
                            пикселей.
                        </p>
                        <Button>Выбрать файл</Button>
                    </div>
                )}
            </Dropzone>
            {altMinSize && <Button style={{ marginTop: '-60px' }} variant="outline-secondary" onClick={toggleSizeBoundary}>Соотношение сторон</Button>}
        </>
    ), [altMinSize, sizeBoundary, toggleSizeBoundary]);

    return (
        <Form.Group className={`fast-image-load ${className}`}>
            <span className="fast-image-load__label" style={labelStyle}>
                {label}
                {required && <span className="fast-image-load__required">*</span>}
            </span>
            {image ? loadedImage : dropZone}
            <StdModalDialog
                isOpen={isDialogActive}
                hideHandler={closeDialog}
                closeHandler={closeDialog}
                title={label}
                body={dialogBody}
                footer={dialogFooter}
                onSave={saveImage}
            />
        </Form.Group>
    );
}

FastImageLoad.defaultProps = {
    sizeBoundary: '0x0',
};

export default FastImageLoad;
